import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { HELP_REGISTRY, resolveHelpKey } from "../help/helpRegistry";

function HelpSection({ title, items }) {
  if (!items?.length) return null;
  return (
    <section className="context-help-section">
      <h4>{title}</h4>
      <ul>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

export default function ContextHelpPanel() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);
  const hadOpenedRef = useRef(false);

  const helpKey = useMemo(() => resolveHelpKey(location), [location]);
  const content = helpKey ? HELP_REGISTRY[helpKey] : null;

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    if (open) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    if (open && dialogRef.current) {
      hadOpenedRef.current = true;
      const focusable = dialogRef.current.querySelector(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      (focusable || dialogRef.current).focus();
      return;
    }

    if (!open && hadOpenedRef.current && triggerRef.current) {
      triggerRef.current.focus();
    }
  }, [open]);

  if (!content) return null;

  return (
    <>
      <button
        type="button"
        className="context-help-trigger"
        onClick={() => setOpen(true)}
        aria-label={`Help on ${content.title}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        ref={triggerRef}
      >
        ? Help on this page
      </button>

      {open && <button type="button" className="context-help-backdrop" aria-label="Close help" onClick={() => setOpen(false)} />}

      <aside
        className={`context-help-drawer ${open ? "open" : ""}`}
        aria-hidden={!open}
        role="dialog"
        aria-modal="true"
        aria-labelledby="context-help-title"
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className="context-help-header">
          <div>
            <p className="context-help-kicker">Contextual help</p>
            <h3 id="context-help-title">{content.title}</h3>
          </div>
          <button type="button" className="context-help-close" onClick={() => setOpen(false)} aria-label="Close help panel">
            ✕
          </button>
        </div>

        <p className="context-help-purpose">{content.purpose}</p>

        <HelpSection title="What you can do here" items={content.actions} />
        <HelpSection title="How to use it" items={content.howTo} />
        <HelpSection title="Things to watch out for" items={content.watchOut} />
      </aside>
    </>
  );
}
